import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronRight, Database, FlaskConical, Play } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useImports } from '@/api/queries'
import type { ImportJob, ImportJobStatus, ImportSource } from '@/api/types'
import { IMPORT_JOB_STATUSES, IMPORT_SOURCES, isImportRunning } from '@/api/types'
import { CursorPager } from '@/components/data/cursor-pager'
import { PageHeader } from '@/components/layout/app-shell'
import { ImportProgress, ImportStatusBadge } from '@/features/imports/import-status'
import { StartImportDialog } from '@/features/imports/start-import-dialog'
import {
  formatDuration,
  formatRelative,
  IMPORT_MODE_LABELS,
  IMPORT_SOURCE_LABELS,
  IMPORT_STATUS_LABELS,
} from '@/lib/format'
import { useCursorPagination } from '@/lib/use-cursor-pagination'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  status: z.enum(IMPORT_JOB_STATUSES).optional(),
  source: z.enum(IMPORT_SOURCES).optional(),
})

export const Route = createFileRoute('/_app/import/')({
  validateSearch: searchSchema,
  component: ImportsPage,
})

function ImportsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [startOpen, setStartOpen] = useState(false)

  const params = {
    ...(search.status ? { status: search.status } : {}),
    ...(search.source ? { source: search.source } : {}),
    limit: 20,
  }

  const pagination = useCursorPagination(JSON.stringify(params))
  const list = useImports({
    ...params,
    ...(pagination.cursor ? { cursor: pagination.cursor } : {}),
  })
  const jobs = list.data?.data ?? []
  const active = jobs.find(isImportRunning)

  return (
    <>
      <PageHeader
        title="Import del catalogo"
        description="Il catalogo si popola da qui. Un solo import può essere attivo per volta."
        actions={
          <Button variant="primary" onClick={() => setStartOpen(true)} disabled={Boolean(active)}>
            <Play />
            Avvia import
          </Button>
        }
      />

      {active ? (
        <Alert variant="info" title="Import in corso" className="mb-4">
          <div className="space-y-2">
            <ImportProgress job={active} />
            <Link
              to="/import/$jobId"
              params={{ jobId: active.id }}
              className="text-secondary-content inline-block font-medium underline-offset-4 hover:underline"
            >
              Apri il dettaglio
            </Link>
          </div>
        </Alert>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex">
        <Select
          aria-label="Filtra per stato del job"
          value={search.status ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                status: value === 'all' ? undefined : (value as ImportJobStatus),
              }),
            })
          }
          className="sm:w-40"
          items={[
            { value: 'all', label: 'Ogni stato' },
            ...IMPORT_JOB_STATUSES.map((status) => ({
              value: status,
              label: IMPORT_STATUS_LABELS[status],
            })),
          ]}
        />

        <Select
          aria-label="Filtra per sorgente"
          value={search.source ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                source: value === 'all' ? undefined : (value as ImportSource),
              }),
            })
          }
          className="sm:w-44"
          items={[
            { value: 'all', label: 'Ogni sorgente' },
            ...IMPORT_SOURCES.map((source) => ({
              value: source,
              label: IMPORT_SOURCE_LABELS[source],
            })),
          ]}
        />
      </div>

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-md" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={<Database />}
          title="Nessun import"
          description="Il primo import popola il catalogo dal dataset seed o dall'API WorkoutX. Conviene partire da una prova."
          action={
            <Button variant="primary" onClick={() => setStartOpen(true)}>
              <Play />
              Avvia import
            </Button>
          }
        />
      ) : (
        <>
          <ul
            className={cn(
              'flex flex-col gap-2',
              'transition-opacity',
              list.isFetching && 'opacity-70',
            )}
          >
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </ul>

          <CursorPager
            meta={list.data?.meta}
            page={pagination.page}
            pageCount={jobs.length}
            canGoBack={pagination.canGoBack}
            onNext={() => pagination.next(list.data?.meta.nextCursor)}
            onPrevious={pagination.previous}
            isFetching={list.isFetching}
            itemLabel="job"
          />
        </>
      )}

      <StartImportDialog open={startOpen} onOpenChange={setStartOpen} />
    </>
  )
}

function JobCard({ job }: { job: ImportJob }) {
  const running = isImportRunning(job)

  return (
    <li>
      <Card radius="md" padding="none">
        <Link
          to="/import/$jobId"
          params={{ jobId: job.id }}
          className="flex items-start gap-3 p-3 sm:p-4"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <ImportStatusBadge status={job.status} />
              <Badge variant="outline">{IMPORT_SOURCE_LABELS[job.source]}</Badge>
              <Badge variant={job.mode === 'replace' ? 'warning' : 'neutral'}>
                {IMPORT_MODE_LABELS[job.mode]}
              </Badge>
              {job.dryRun ? (
                <Badge variant="secondary">
                  <FlaskConical />
                  Prova
                </Badge>
              ) : null}
            </div>

            {running ? (
              <ImportProgress job={job} />
            ) : (
              <p className="text-muted-foreground text-sm">
                {job.progress.created > 0 || job.progress.updated > 0 ? (
                  <>
                    {job.progress.created} creati · {job.progress.updated} aggiornati
                    {job.progress.failed > 0 ? ` · ${job.progress.failed} falliti` : ''}
                  </>
                ) : (
                  'Nessuna scrittura'
                )}
              </p>
            )}

            <p className="text-muted-foreground text-xs">
              Avviato {formatRelative(job.startedAt ?? job.createdAt)}
              {job.startedAt && job.finishedAt
                ? ` · durata ${formatDuration(job.startedAt, job.finishedAt)}`
                : ''}
              {job.warnings && job.warnings.length > 0
                ? ` · ${job.warnings.length} ${job.warnings.length === 1 ? 'avviso' : 'avvisi'}`
                : ''}
            </p>
          </div>

          <ChevronRight className="text-muted-foreground mt-1 size-4 shrink-0" aria-hidden />
        </Link>
      </Card>
    </li>
  )
}
